/**
 * Personalization Utilities
 * Helper functions for child name tokens and age-based UI logic
 */

/**
 * Replace {{child_name}} token in text with actual child name
 * @param text - Text containing {{child_name}} token
 * @param name - Child's name (optional)
 * @returns Text with token replaced, or original if no name
 */
export function childNameToken(text: string, name?: string | null): string {
  if (!name || !text.includes('{{child_name}}')) {
    return text;
  }
  return text.replaceAll('{{child_name}}', name);
}

/**
 * Generate "for [name]" text for UI
 * @param name - Child's name (optional)
 * @returns "for Emma" or empty string
 */
export function childFor(name?: string | null): string {
  return name ? `for ${name}` : '';
}

/**
 * Check if age picker should be hidden in creation flows
 * @param ageRange - User's kid age range setting
 * @returns true if age picker should be hidden
 */
export function shouldHideAgePicker(ageRange?: string | null): boolean {
  return !!ageRange;
}

/**
 * Check if name input should be hidden in creation flows
 * @param name - User's kid name setting
 * @returns true if name input should be hidden
 */
export function shouldHideNameInput(name?: string | null): boolean {
  return !!name;
}

/**
 * Format age range enum to display text
 * @param ageRange - Age range enum value
 * @returns Display text like "0-1" or "5-6"
 */
export function formatAgeRange(ageRange?: string | null): string {
  if (!ageRange) return '';
  
  const ageMap: Record<string, string> = {
    AGE_0_1: '0-1',
    AGE_1_2: '1-2',
    AGE_3_4: '3-4',
    AGE_5_6: '5-6',
    AGE_7_8: '7-8',
    AGE_9_10: '9-10',
  };
  
  return ageMap[ageRange] || '';
}

/**
 * Get age bucket string from age range enum (for legacy compatibility)
 * @param ageRange - Age range enum value
 * @returns Age bucket like "0-1" or null
 */
export function getAgeBucketFromRange(ageRange?: string | null): string | null {
  if (!ageRange) return null;
  return formatAgeRange(ageRange) || null;
}

